import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { MapPin, User, Phone, Banknote, Clock, Loader, Package, AlertCircle, Calendar, Wallet, AlertTriangle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { showErrorToast } from '../../utils/errorHandler';
import DriverLayout from '../../components/driver/DriverLayout';

// Helper function to format scheduled date/time
const formatScheduledTime = (scheduledAt) => {
  if (!scheduledAt) return '';
  const date = new Date(scheduledAt);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const isTomorrow = date.toDateString() === new Date(now.getTime() + 86400000).toDateString();
  
  const timeStr = date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  
  if (isToday) {
    return `Today at ${timeStr}`;
  } else if (isTomorrow) {
    return `Tomorrow at ${timeStr}`;
  } else {
    const dateStr = date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric'
    });
    return `${dateStr} at ${timeStr}`;
  }
};

const DriverRides = () => {
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);
  const [hasActiveRide, setHasActiveRide] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);

  useEffect(() => {
    fetchAvailableRides();
    checkActiveRide();
    fetchWalletBalance();
    const interval = setInterval(() => {
      fetchAvailableRides();
      checkActiveRide();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchWalletBalance = async () => {
    try {
      const token = localStorage.getItem('tekstar_token');
      if (!token) return;
      const api = (await import('../../utils/api')).default;
      const res = await api.get('/drivers/profile');
      setWalletBalance(res.data.driver?.walletBalance ?? 0);
    } catch (e) { /* silent */ }
  };

  const checkActiveRide = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/drivers/my-rides?status=accepted');
      const activeRides = response.data.rides || [];
      
      // Also check for in_progress rides
      const inProgressResponse = await api.get('/drivers/my-rides?status=in_progress');
      const inProgressRides = inProgressResponse.data.rides || [];
      
      setHasActiveRide(activeRides.length > 0 || inProgressRides.length > 0);
    } catch (error) {
      console.error('Error checking active rides:', error);
    }
  };

  const fetchAvailableRides = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/drivers/available-rides');
      setRides(response.data.rides || []);
      // Store any status message from backend (e.g., "You must be online to see available rides")
      setStatusMessage(response.data.message || null);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching rides:', error);
      if (loading) {
        showErrorToast(toast, error, 'Failed to load available rides');
        setLoading(false);
      }
    }
  };

  const handleAcceptRide = async (rideId) => {
    setAccepting(rideId);
    
    try {
      const api = (await import('../../utils/api')).default;
      await api.post('/drivers/ride-action', {
        rideId: rideId,
        action: 'accept'
      });

      toast({
        title: 'Ride Accepted!',
        description: 'Navigating to active ride...',
      });

      // Find the accepted ride data
      const acceptedRide = rides.find(r => r.id === rideId);

      // Navigate to active ride page
      navigate('/driver/active-ride', { state: { ride: acceptedRide } });
    } catch (error) {
      console.error('Error accepting ride:', error);
      const detail = error.response?.data?.detail || '';
      if (detail.toLowerCase().includes('wallet') || detail.toLowerCase().includes('balance') || detail.toLowerCase().includes('insufficient')) {
        toast({
          title: 'Insufficient Wallet Balance',
          description: 'Your wallet balance is too low to accept this ride. Please top up first.',
          variant: 'destructive',
          action: (
            <Button size="sm" variant="outline" onClick={() => navigate('/driver/wallet')} className="border-white text-white hover:bg-white/20">
              Top Up
            </Button>
          ),
        });
        setWalletBalance(0);
      } else {
        showErrorToast(toast, error, 'Failed to accept ride');
      }
      setAccepting(null);
      fetchAvailableRides();
    }
  };

  const handleRejectRide = async (rideId) => {
    try {
      const api = (await import('../../utils/api')).default;
      await api.post('/drivers/ride-action', {
        rideId: rideId,
        action: 'reject'
      });

      // Remove from list
      setRides(rides.filter(r => r.id !== rideId));

      toast({
        title: 'Ride Rejected',
        description: 'Ride removed from your list',
      });
    } catch (error) {
      console.error('Error rejecting ride:', error);
    }
  };

  if (loading) {
    return (
      <DriverLayout>
        <div className="flex items-center justify-center h-96">
          <Loader className="h-12 w-12 animate-spin text-pink-600" />
        </div>
      </DriverLayout>
    );
  }

  return (
    <DriverLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Available Requests</h1>
            <p className="text-gray-600 mt-1">Accept rides & deliveries to start earning</p>
          </div>
        </div>

        {/* Zero Wallet Balance Warning */}
        {walletBalance !== null && walletBalance <= 0 && !hasActiveRide && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="rides-wallet-warning">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-800">Cannot Accept Rides — Zero Balance</h3>
              <p className="text-sm text-red-700 mt-1">Top up your wallet to start accepting rides. A 20% commission is deducted per completed ride.</p>
              <Button onClick={() => navigate('/driver/wallet')} className="mt-2 bg-red-600 hover:bg-red-700 text-white" size="sm" data-testid="rides-topup-btn">
                <Wallet className="h-4 w-4 mr-2" /> Top Up Wallet
              </Button>
            </div>
          </div>
        )}

        {/* Active Ride Warning */}
        {hasActiveRide && (
          <Card className="p-6 bg-orange-50 border-orange-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-orange-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900">You Have an Active Ride</h3>
                <p className="text-sm text-orange-700 mt-1">
                  Please complete your current ride before accepting a new one. You can only handle one ride at a time.
                </p>
                <Button
                  onClick={() => navigate('/driver/active-ride')}
                  className="mt-3 bg-orange-600 hover:bg-orange-700"
                  size="sm"
                >
                  Go to Active Ride
                </Button>
              </div>
            </div>
          </Card>
        )}

        <div className="flex items-center justify-between">
          <Button
            onClick={fetchAvailableRides}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Clock className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {rides.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <MapPin className="h-8 w-8 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Available Requests</h2>
            {statusMessage ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-yellow-800 font-medium">{statusMessage}</p>
              </div>
            ) : (
              <p className="text-gray-600 mb-4">
                There are no rides or deliveries available at the moment. Check back soon!
              </p>
            )}
            <p className="text-sm text-gray-500">
              Make sure you are online to receive ride requests
            </p>
            <Button
              onClick={() => navigate('/driver/dashboard')}
              className="mt-4 bg-pink-600 hover:bg-pink-700"
            >
              Go to Dashboard
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {rides.map((ride) => (
              <Card key={ride.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-4">
                    {/* Type Badge */}
                    <div className="flex flex-wrap gap-2">
                      {ride.type === 'delivery' && (
                        <Badge className="bg-orange-500 text-white">
                          <Package className="h-3 w-3 mr-1" />
                          Package Delivery
                        </Badge>
                      )}
                      {ride.isScheduled && (
                        <Badge className="bg-blue-600 text-white" data-testid="scheduled-ride-badge">
                          <Calendar className="h-3 w-3 mr-1" />
                          Scheduled Ride
                        </Badge>
                      )}
                      {ride.isAutoAssigned && (
                        <Badge className="bg-green-500 text-white animate-pulse">
                          <Clock className="h-3 w-3 mr-1" />
                          Auto-Assigned - {Math.floor((ride.timeoutSecondsRemaining || 0) / 60)}:{String((ride.timeoutSecondsRemaining || 0) % 60).padStart(2, '0')} left
                        </Badge>
                      )}
                    </div>
                    
                    {/* Scheduled Time Display */}
                    {ride.isScheduled && ride.scheduledAt && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center gap-2" data-testid="scheduled-time-display">
                        <Clock className="h-4 w-4 text-blue-600" />
                        <span className="text-blue-800 font-medium">
                          Pickup: {formatScheduledTime(ride.scheduledAt)}
                        </span>
                      </div>
                    )}

                    {/* Sender/Rider Info */}
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                        {ride.userName?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {ride.userName || (ride.type === 'delivery' ? 'Sender' : 'Rider')}
                        </p>
                        {ride.userPhone && (
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Phone className="h-3 w-3" />
                            {ride.userPhone}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Package Details (for deliveries) */}
                    {ride.type === 'delivery' && ride.packageDetails && (
                      <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="h-4 w-4 text-orange-600" />
                          <span className="font-medium text-orange-900">Package Info</span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <p>
                            <span className="text-gray-600">Size:</span>{' '}
                            <span className="font-medium capitalize">{ride.packageDetails.size}</span>
                          </p>
                          <p>
                            <span className="text-gray-600">Description:</span>{' '}
                            <span className="font-medium">{ride.packageDetails.description}</span>
                          </p>
                          {ride.packageDetails.fragile && (
                            <div className="flex items-center gap-1 text-orange-600">
                              <AlertCircle className="h-3 w-3" />
                              <span className="font-medium">Fragile - Handle with care</span>
                            </div>
                          )}
                          {ride.receiverDetails && (
                            <p className="pt-2 border-t border-orange-200">
                              <span className="text-gray-600">Receiver:</span>{' '}
                              <span className="font-medium">{ride.receiverDetails.name}</span>
                              <span className="text-gray-500"> • {ride.receiverDetails.phone}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Route */}
                    <div className="space-y-2">
                      <div className="flex items-start gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full mt-1.5"></div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-600">
                            {ride.type === 'delivery' ? 'Pickup Location' : 'Pickup'}
                          </p>
                          <p className="font-medium text-gray-900">{ride.pickup}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-3 h-3 bg-red-500 rounded-full mt-1.5"></div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-600">
                            {ride.type === 'delivery' ? 'Delivery Location' : 'Dropoff'}
                          </p>
                          <p className="font-medium text-gray-900">{ride.dropoff}</p>
                        </div>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="flex items-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <Banknote className="h-4 w-4 text-green-600" />
                        <span className="font-semibold text-green-600">K{ride.price}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-600" />
                        <span className="text-gray-600">{ride.distance?.toFixed(1) || 'N/A'} km</span>
                      </div>
                      <Badge variant="outline">{ride.rideTypeId || 'Standard'}</Badge>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 ml-4">
                    <Button
                      onClick={() => handleAcceptRide(ride.id)}
                      disabled={accepting === ride.id || hasActiveRide}
                      className={`${ride.isAutoAssigned ? 'bg-green-600 hover:bg-green-700 animate-pulse' : 'bg-pink-600 hover:bg-pink-700'} disabled:bg-gray-400 disabled:cursor-not-allowed`}
                      title={hasActiveRide ? "Complete your current ride first" : ride.isAutoAssigned ? "Confirm auto-assigned ride" : "Accept this ride"}
                    >
                      {accepting === ride.id ? 'Accepting...' : hasActiveRide ? 'Unavailable' : ride.isAutoAssigned ? 'Confirm' : 'Accept'}
                    </Button>
                    {ride.isAutoAssigned && !hasActiveRide && (
                      <Button
                        onClick={async () => {
                          try {
                            const api = (await import('../../utils/api')).default;
                            await api.post('/drivers/ride-action', {
                              rideId: ride.id,
                              action: 'reject'
                            });
                            toast({
                              title: 'Ride Rejected',
                              description: 'Finding another driver...',
                            });
                            fetchAvailableRides();
                          } catch (error) {
                            toast({
                              title: 'Error',
                              description: error.response?.data?.detail || 'Failed to reject ride',
                              variant: 'destructive',
                            });
                          }
                        }}
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        Reject
                      </Button>
                    )}
                    {!ride.isAutoAssigned && (
                      <Button
                        onClick={() => handleRejectRide(ride.id)}
                        disabled={accepting === ride.id}
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                      >
                        Reject
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Info */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <p className="text-sm text-blue-800">
            💡 <strong>Tip:</strong> Accept rides quickly to maximize your earnings. Commission of 20% will be deducted from your wallet upon completion.
          </p>
        </Card>
      </div>
    </DriverLayout>
  );
};

export default DriverRides;